import { NavButton } from '@/components/nav-button';
import { TooltipText } from '@/components/tooltip-text';
import { getVaultMetadataByAddress } from '@/constants/nucleus-vaults';
import { AppRoute, ROYCO_DOMAIN_URL } from '@/constants/shared';
import { useVaultTvl } from '@/hooks/useVaultTvl';
import { NucleusClient } from '@/network/clients/nucleus';
import { lowercaseAddressKeys } from '@/utils';
import {
  Box,
  Card,
  FormatNumber,
  Heading,
  HStack,
  Icon,
  Image,
  Separator,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useAppKitNetwork } from '@reown/appkit/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { PiSealCheckFill } from 'react-icons/pi';
import { useNavigate, useParams } from 'react-router';
import { Composition } from './Composition';
import { PLUME_INCENTIVES_WITHDRAW_TEXT } from './constants';
import { Balance } from './DepositsAndWithdrawals/VaultBalance';
import { HOW_IT_WORKS_ACCORDION_VALUE, HowDoesTheVaultWorkAccordion } from './HowDoesTheVaultWorkAccordion';
import { IncentivesList } from './IncentivesList';
import { MobileVaultBalance } from './MobileVaultBalance';
import { RoycoPositions } from './RoycoPositions';
import { VaultHistory } from './VaultHistory';

const client = new NucleusClient();
export const OptimizerVault = () => {
  const navigate = useNavigate();
  const { vaultAddress } = useParams();
  const { chainId } = useAppKitNetwork();
  const [value, setValue] = useState<string[]>();

  const howItWorksAccordionRef = useRef<HTMLDivElement>(null);

  const onClickHowItWorks = useCallback(() => {
    setValue([HOW_IT_WORKS_ACCORDION_VALUE]);
    howItWorksAccordionRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const vaultMetadata = getVaultMetadataByAddress(vaultAddress) ?? {};
  const { name, summary, marketId, yieldGeneratingTokenSymbol, iconPath, learnMoreLink } = vaultMetadata;

  const { tvlData } = useVaultTvl({ vaultAddress: vaultMetadata.vaultAddress });
  const { data: roycoMarketData } = useQuery({
    queryKey: ['roycoMarketData', vaultAddress],
    queryFn: async () => {
      if (!vaultAddress || !marketId) return {};
      return client.get('plume/market-data', {
        marketId,
        vaultAddress,
      });
    },
    enabled: Boolean(marketId),
  });

  const { data: netApyResponse } = useQuery({
    queryKey: ['vaultNetApy', vaultAddress],
    queryFn: async () => {
      if (!vaultAddress) return null;
      const apyResponse = await client.get('plume/net-apy', {
        vaultAddress,
      });
      return apyResponse;
    },
  });

  const netApy = netApyResponse?.netApy;

  const { data: vaultBaseApy } = useQuery({
    queryKey: ['vaultBaseApy', vaultAddress],
    queryFn: async () => {
      if (!vaultAddress) return 0;
      const res = await client.get('vaults/apy', {
        tokenAddress: vaultAddress,
      });
      if (res.error) throw new Error(res.error);
      return res.apy;
    },
  });

  const effectiveNetApy = netApy ?? vaultBaseApy ?? 0;

  if (!vaultAddress) {
    navigate(AppRoute.Optimizer);
    return;
  }
  const tvl = tvlData?.tvl_in_usd;
  const lowercasedLockups = lowercaseAddressKeys(roycoMarketData?.lockup ?? {});
  const { forfeitableDuration = 0 } = lowercasedLockups[vaultAddress.toLowerCase()]?.[marketId.toLowerCase()] ?? {};

  return (
    <Box spaceY={[6, 6, 12]}>
      <HStack gap={3}>
        <NavButton direction="bk" href={AppRoute.Optimizer} h={8} w={12} bgColor="bg" />
        <Spacer flex={1} />
        <HStack gap={1}>
          <Text fontSize="lg" fontFamily="heading" color="fg.muted" fontWeight="500">
            TVL
          </Text>
          <Text fontSize="lg" fontFamily="heading" fontWeight="500">
            {tvl ? <FormatNumber value={tvl} style="currency" currency="USD" /> : '--'}
          </Text>
        </HStack>
        <NavButton href={`${ROYCO_DOMAIN_URL}/market/${chainId}/0/${marketId}`} target="blank" bgColor="bg">
          Royco Market <FiArrowUpRight />
        </NavButton>
      </HStack>
      <Box hideFrom="md">
        <MobileVaultBalance />
      </Box>
      <Card.Root borderRadius="3xl" p={0}>
        <Card.Body p={0}>
          <HStack gap={0}>
            {/* This is the top left card, the VaultSummary */}
            <Box spaceY={6} flex={1} py={[6, 6, 8]} px={[4, 4, 8]}>
              <HStack justifyContent="space-between">
                <Image src={iconPath} boxSize={16} />
                <NavButton href={learnMoreLink} variant="subtle">
                  Learn about {yieldGeneratingTokenSymbol}
                  <FiArrowUpRight />
                </NavButton>
              </HStack>
              <Box spaceY={3}>
                <HStack>
                  <Heading size={['xl', 'xl', '2xl']} fontWeight="700">
                    {name}
                  </Heading>
                  <Icon color="#00827B" boxSize={6}>
                    <PiSealCheckFill />
                  </Icon>
                </HStack>
                <Text fontSize="md" color="fg.muted" fontWeight="500">
                  {summary}
                </Text>
              </Box>

              <Stack gap={4} direction={['column', 'column', 'row']}>
                <Card.Root bgColor="#F2F9F8" border="none" flex={1} borderRadius="xl">
                  <Card.Body spaceY={1} p={[4, 4, 5]}>
                    <Text fontSize="sm" color="fg.muted" fontWeight="500">
                      NET APY
                    </Text>
                    <Text fontSize="lg" fontWeight="500" color="#00827B">
                      <FormatNumber
                        value={effectiveNetApy}
                        style="percent"
                        maximumFractionDigits={2}
                        minimumFractionDigits={2}
                      />
                    </Text>
                  </Card.Body>
                </Card.Root>
                <Card.Root bgColor="bg.subtle" border="none" flex={1} borderRadius="xl">
                  <Card.Body spaceY={1} p={[4, 4, 5]}>
                    <Text fontSize="sm" color="fg.muted">
                      PLUME Incentives
                    </Text>
                    <TooltipText
                      text={`${forfeitableDuration} day forfeit period`}
                      content={PLUME_INCENTIVES_WITHDRAW_TEXT}
                      fontSize="lg"
                      fontWeight="500"
                      color="fg"
                    />
                  </Card.Body>
                </Card.Root>
              </Stack>
              <HStack>
                <Text fontSize="sm" color="fg.muted" fontWeight="500">
                  Powered by
                </Text>
                <HStack>
                  <Image src="/assets/vaults/nucleus.svg" />
                  Nucleus
                </HStack>
                <HStack>
                  <Image src="/assets/vaults/royco.svg" />
                  Royco
                </HStack>
              </HStack>
              <IncentivesList vaultMetadata={vaultMetadata} />
            </Box>
            {/* End of VaultSummary */}
            <Separator orientation="vertical" bgColor="bg.muted" alignSelf="stretch" hideBelow="md" />
            <Box flex={1} p={8} alignSelf="stretch" spaceY={8} hideBelow="md">
              <Balance onClickHowItWorks={onClickHowItWorks} />
            </Box>
          </HStack>
        </Card.Body>
      </Card.Root>
      <RoycoPositions />
      <VaultHistory vaultMetadata={vaultMetadata} />
      <HowDoesTheVaultWorkAccordion
        vaultMetadata={vaultMetadata}
        value={value}
        setValue={setValue}
        ref={howItWorksAccordionRef}
      />
      <Composition vaultMetadata={vaultMetadata} />
    </Box>
  );
};
